import React from 'react';
import { Badge, Box, Flex, Icon, Stack, Text } from '@chakra-ui/react';
import {
  FiAlertOctagon,
  FiAlertTriangle,
  FiRepeat,
  FiSliders,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

import {
  AlertType,
  FeedAnalysisAlert,
} from '../../../../services/feedAnalysisService';

const TYPE_INFO: Record<AlertType, { label: string; icon: typeof FiSliders }> =
  {
    out_of_range: { label: 'Fora da faixa', icon: FiAlertTriangle },
    recurring: { label: 'Desvio repetido', icon: FiRepeat },
    no_reference: { label: 'Sem referência', icon: FiSliders },
    feed_without_formula: { label: 'Ração sem fórmula', icon: FiSliders },
  };

interface AlertsListProps {
  alerts: FeedAnalysisAlert[];
  emptyText?: string;
  limit?: number;
}

export const AlertsList: React.FC<AlertsListProps> = ({
  alerts,
  emptyText = 'Nenhum alerta neste mês.',
  limit,
}) => {
  const visible = limit ? alerts.slice(0, limit) : alerts;

  if (!alerts.length) {
    return (
      <Text color="gray.400" fontSize="sm">
        {emptyText}
      </Text>
    );
  }

  return (
    <Stack spacing="2">
      {visible.map(alert => {
        const info = TYPE_INFO[alert.type];
        const critical = alert.severity === 'critical';
        const content = (
          <Flex
            align="flex-start"
            gridGap="3"
            bg="gray.800"
            borderWidth="1px"
            borderColor={critical ? 'red.500' : 'gray.700'}
            borderLeftWidth="4px"
            borderLeftColor={critical ? 'red.500' : 'yellow.400'}
            borderRadius="md"
            p="3"
            _hover={alert.sample_id ? { bg: 'gray.700' } : undefined}
          >
            <Icon
              as={critical ? FiAlertOctagon : info.icon}
              color={critical ? 'red.400' : 'yellow.300'}
              mt="1"
              flexShrink={0}
            />
            <Box flex="1" minW="0">
              <Flex gridGap="2" align="center" flexWrap="wrap" mb="1">
                <Badge colorScheme={critical ? 'red' : 'yellow'}>
                  {critical ? 'Crítico' : 'Atenção'}
                </Badge>
                <Text fontSize="xs" color="gray.400" fontWeight="600">
                  {info.label}
                </Text>
              </Flex>
              <Text fontSize="sm">{alert.message}</Text>
            </Box>
          </Flex>
        );

        return alert.sample_id ? (
          <Link key={alert.id} to={`/feed-analysis/samples/${alert.sample_id}`}>
            {content}
          </Link>
        ) : (
          <Box key={alert.id}>{content}</Box>
        );
      })}
      {limit && alerts.length > limit && (
        <Text fontSize="sm" color="gray.400">
          + {alerts.length - limit} alerta(s)
        </Text>
      )}
    </Stack>
  );
};
